import { EnumsOf } from "@/lib/utils"

// ============================================
// Enums and Constants
// ============================================

export const V2MarketType = {
  DOMESTIC: "DOMESTIC",
  EXPORT: "EXPORT",
  BOTH: "BOTH",
} as const
export type V2MarketType = EnumsOf<typeof V2MarketType>

export const V2ParameterSection = {
  INFORMATION_FIELDS: "INFORMATION_FIELDS",
  LABELLING_COMPLIANCE: "LABELLING_COMPLIANCE",
  MACHINE_CHECK: "MACHINE_CHECK",
  PRIMARY_PACKAGING: "PRIMARY_PACKAGING",
  SECONDARY_PACKAGING: "SECONDARY_PACKAGING",
} as const
export type V2ParameterSection = EnumsOf<typeof V2ParameterSection>

export const V2SheetStatus = {
  DRAFT: "DRAFT",
  SUBMITTED: "SUBMITTED",
  COMPLETED: "COMPLETED",
  DELETED: "DELETED",
} as const
export type V2SheetStatus = EnumsOf<typeof V2SheetStatus>

export const V2ParameterMode = {
  INFO: "INFO",
  TEST: "TEST",
} as const
export type V2ParameterMode = EnumsOf<typeof V2ParameterMode>

export const V2InputType = {
  VALUE_TEXT: "VALUE_TEXT",
  VALUE_NUMBER: "VALUE_NUMBER",
  CHOICE: "CHOICE",
  DATE: "DATE",
  DROPDOWN: "DROPDOWN",
  RADIO: "RADIO",
} as const
export type V2InputType = EnumsOf<typeof V2InputType>

export const V2ValidationType = {
  CHOICE: "CHOICE",
  LIMIT: "LIMIT",
  FLAG: "FLAG",
  NONE: "NONE",
} as const
export type V2ValidationType = EnumsOf<typeof V2ValidationType>

// Display names for sections
export const V2SectionDisplayNames: Record<V2ParameterSection, string> = {
  [V2ParameterSection.INFORMATION_FIELDS]: "Information Fields",
  [V2ParameterSection.LABELLING_COMPLIANCE]: "Labelling Compliance",
  [V2ParameterSection.MACHINE_CHECK]: "Machine Check",
  [V2ParameterSection.PRIMARY_PACKAGING]: "Primary Packaging",
  [V2ParameterSection.SECONDARY_PACKAGING]: "Secondary Packaging",
}

// Section order for display
export const V2SectionOrder: V2ParameterSection[] = [
  V2ParameterSection.INFORMATION_FIELDS,
  V2ParameterSection.LABELLING_COMPLIANCE,
  V2ParameterSection.MACHINE_CHECK,
  V2ParameterSection.PRIMARY_PACKAGING,
  V2ParameterSection.SECONDARY_PACKAGING,
]

// ============================================
// API Schema Types (from backend)
// ============================================

/**
 * Parameter item returned in schema from API
 */
export interface V2ParameterItem {
  id: string
  name: string
  mode: V2ParameterMode
  inputType: V2InputType
  validationType: V2ValidationType | null
  possibleValues: string[]
  correctValue: string | null
  minLimit: number | null
  maxLimit: number | null
  unit: string | null
  frequencyMinutes: 60 | 120 | 180 | 240 | null
  marketType: V2MarketType
  required: boolean
  sequenceOrder: number
}

/**
 * Parameters grouped by section
 */
export interface V2ParametersBySection {
  INFORMATION_FIELDS: V2ParameterItem[]
  LABELLING_COMPLIANCE: V2ParameterItem[]
  MACHINE_CHECK: V2ParameterItem[]
  PRIMARY_PACKAGING: V2ParameterItem[]
  SECONDARY_PACKAGING: V2ParameterItem[]
}

/**
 * SKU with its parameters
 */
export interface V2SKU {
  id: string
  name: string
  code: string
  parameters: V2ParametersBySection
}

/**
 * Product with its SKUs
 */
export interface V2Product {
  id: string
  name: string
  skus: V2SKU[]
}

/**
 * Machine with product IDs
 */
export interface V2Machine {
  id: string
  name: string
  products: string[] // Array of product IDs
}

/**
 * Department schema
 */
export interface V2Department {
  id: string
  name: string
  machines: V2Machine[]
}

/**
 * Products dictionary keyed by product ID
 */
export type V2ProductsMap = Record<string, V2Product>

/**
 * All department schemas response from API
 */
export interface V2AllSchemasResponse {
  departments: V2Department[]
  products: V2ProductsMap
}

/**
 * Single department schema response from API
 */
export interface V2SingleDeptSchemaResponse {
  department: V2Department
  products: V2ProductsMap
}

// ============================================
// Request Types
// ============================================

/**
 * Start shift request body
 */
export interface StartV2ShiftRequest {
  processId: string
  shiftId: string
}

/**
 * Window reading for frequency-based parameters
 */
export interface SubmitWindowReading {
  value: string
  startTime: string // ISO datetime
  endTime: string // ISO datetime
  recordedAt: string // ISO datetime
  remark?: string
}

/**
 * Single parameter reading for submission
 */
export interface SubmitReading {
  parameterId: string
  value: string
  remark?: string
  recordedAt: string // ISO datetime
  windowReadings?: SubmitWindowReading[]
}

/**
 * Single item (batch) in a sheet
 */
export interface SubmitItem {
  productId: string
  skuId: string
  marketType: V2MarketType
  sequenceOrder: number
  readings: SubmitReading[]
}

/**
 * Single sheet submission
 */
export interface SubmitSheetRequest {
  departmentId: string
  machineId: string
  items: SubmitItem[]
  status: V2SheetStatus
}

/**
 * Request body is array of sheets
 */
export type SubmitV2SheetsRequest = SubmitSheetRequest[]

/**
 * Delete item request body
 */
export interface DeleteV2ItemRequest {
  departmentId: string
  machineId: string
  productId: string
  skuId: string
  sequenceOrder: number
}

// ============================================
// Response Types
// ============================================

/**
 * Window reading detail from API
 */
export interface WindowReadingDetail {
  id: string
  value: string
  startTime: string
  endTime: string
  recordedAt: string
  overtime: boolean | null
  early: boolean | null
  remark?: string
}

/**
 * Single reading detail from API
 */
export interface ReadingDetail {
  id: string
  parameterId: string
  parameterName: string
  value: string
  remark?: string
  recordedAt: string
  unit?: string
  minLimit?: number
  maxLimit?: number
  validationStatus?: "VALID" | "BELOW_LIMIT" | "ABOVE_LIMIT"
  windowReadings?: WindowReadingDetail[]
}

/**
 * Readings grouped by section
 */
export interface SectionReadings {
  section: V2ParameterSection
  readings: ReadingDetail[]
}

/**
 * Item detail from API
 */
export interface ItemDetail {
  id: string
  sequenceOrder: number
  productId: string
  productName: string
  skuId: string
  skuName: string
  skuCode: string
  marketType: V2MarketType
  sections: SectionReadings[]
}

/**
 * Sheet details response
 */
export interface GetV2SheetDetailsResponse {
  sheetId: string
  status: V2SheetStatus
  machineName: string
  machineId: string
  departmentName: string
  departmentId: string
  plantName: string
  plantId: string
  employeeName: string
  shiftName: string
  shiftStartTime: string
  shiftEndTime: string
  createdAt: string
  updatedAt: string
  items: ItemDetail[]
}

/**
 * Last history sheet
 */
export interface LastHistorySheet {
  sheetId: string
  machineName: string
  machineId: string
  status: V2SheetStatus
  employeeName: string
  shiftName: string
  updatedAt: string
  items: ItemDetail[]
}

/**
 * Last history response
 */
export interface GetV2LastHistoryResponse {
  sheets: LastHistorySheet[]
}

/**
 * Sheet list item for queries
 */
export interface SheetListItem {
  id: string
  date: string
  employeeName?: string
  shiftName?: string
  departmentName: string
  machineName: string
  plantName: string
  status: V2SheetStatus
  itemCount: number
}

/**
 * Get sheets response
 */
export interface GetV2SheetsResponse {
  sheets: SheetListItem[]
}

// ============================================
// Pending Sheets Types (for multi-device sync)
// ============================================

/**
 * Pending sheet item (similar to ItemDetail but without id)
 */
export interface PendingSheetItem {
  sequenceOrder: number
  productId: string
  productName: string
  skuId: string
  skuName: string
  skuCode: string
  marketType: V2MarketType
  sections: SectionReadings[]
}

/**
 * Pending sheet
 */
export interface PendingSheet {
  sheetId: string
  departmentId: string
  departmentName: string
  machineId: string
  machineName: string
  status: V2SheetStatus
  items: PendingSheetItem[]
}

/**
 * Get pending sheets response
 */
export interface GetPendingSheetsResponse {
  sheets: PendingSheet[]
}

// ============================================
// Utility Types
// ============================================

/**
 * Check if a parameter should be visible for a given market type
 */
export function isParameterVisibleForMarket(
  paramMarketType: V2MarketType,
  selectedMarketType: V2MarketType
): boolean {
  if (paramMarketType === V2MarketType.BOTH) {
    return true
  }
  return paramMarketType === selectedMarketType
}

/**
 * Get all sections from a V2ParametersBySection object
 */
export function getAllSectionsFromParams(
  params: V2ParametersBySection
): { section: V2ParameterSection; parameters: V2ParameterItem[] }[] {
  return V2SectionOrder.map((section) => ({
    section,
    parameters: params[section] || [],
  }))
}
